import { create } from 'zustand';
import { TEMPLATE_LIST } from '../Components/Dashboard/layoutTemplates';
import { DASHBOARD_VIEWS } from '../Components/Dashboard/dashboardViews';

export const STORAGE_KEY = 'spx_dashboard_config_v7';
export const V6_STORAGE_KEY = 'spx_dashboard_config_v6';
export const LEGACY_STORAGE_KEY = 'spx_dashboard_config_v5';
export const DEFAULT_ACTIVE_VIEW_ID = 'flowmapBase';

const RETIRED_WIDGET_SET = new Set([
  'analytics',
  'analyticsBar',
  'atmBattle',
  // Archived Bookmap variants. See src/Components/Widgets/_retired/README.md for their IDs,
  // exports, dependencies, and restoration steps.
  'bookmapLcEsSandbox',
  // TD062 replaced this surface with `flowmap`. Saved layouts drop stale IDs;
  // no ID rewrite is required for this single-user dashboard.
  'bookmapLcPrimitives',
  'bookmapLcSandbox',
  'crossovers',
  'delta',
  'flowGex',
  'keyLevels',
  // Replay is a global mode. See src/Components/Widgets/_retired/README.md for archived
  // widget restoration and the retained legacy ID mapping.
  'playback',
  'playbackRecorder',
  'position',
  'regime',
  'setupStatus',
]);

const KNOWN_LAYOUT_IDS = new Set(TEMPLATE_LIST.map((template) => template.id));
// One-time compatibility for V5 and V6 migration. V7 treats this shape as a
// valid customization and applies this constant only while reading an older key.
const LEGACY_FIRST_FLOWS_DEFAULT = {
  layoutId: 'tri-right',
  panels: [
    { id: 'p1', widgets: ['flowmap'], activeTab: 'flowmap' },
    { id: 'p2', widgets: ['miFeed'], activeTab: 'miFeed' },
    { id: 'p3', widgets: ['deltaFlowMeter'], activeTab: 'deltaFlowMeter' },
  ],
};

export function sanitizePanels(panels = []) {
  return panels.map((panel) => {
    const widgets = (panel.widgets || []).filter((id) => !RETIRED_WIDGET_SET.has(id));
    const activeTab = widgets.includes(panel.activeTab)
      ? panel.activeTab
      : (widgets[0] ?? null);

    return {
      ...panel,
      widgets,
      activeTab,
    };
  });
}

function clonePanels(panels = []) {
  return sanitizePanels(
    panels.map((panel) => ({
      ...panel,
      widgets: [...(panel.widgets || [])],
    }))
  );
}

function isKnownViewId(viewId) {
  return Object.prototype.hasOwnProperty.call(DASHBOARD_VIEWS, viewId);
}

function isValidViewConfig(config) {
  return Boolean(
    config
    && KNOWN_LAYOUT_IDS.has(config.layoutId)
    && Array.isArray(config.panels)
    && config.panels.length > 0
  );
}

function hasSameValue(value, expected) {
  if (Array.isArray(value) || Array.isArray(expected)) {
    return Array.isArray(value)
      && Array.isArray(expected)
      && value.length === expected.length
      && value.every((item, index) => hasSameValue(item, expected[index]));
  }

  if (value && expected && typeof value === 'object' && typeof expected === 'object') {
    const keys = Object.keys(value);
    const expectedKeys = Object.keys(expected);
    return keys.length === expectedKeys.length
      && keys.every((key) => (
        Object.prototype.hasOwnProperty.call(expected, key)
        && hasSameValue(value[key], expected[key])
      ));
  }

  return value === expected;
}

function hasSameViewConfig(config, expected) {
  return hasSameValue(
    { layoutId: config?.layoutId, panels: config?.panels },
    expected,
  );
}

function isCurrentViewDefault(viewId, config) {
  return isKnownViewId(viewId)
    && hasSameViewConfig(config, getDashboardViewDefault(viewId));
}

function isLegacyFirstFlowsDefault(viewId, config) {
  return viewId === 'firstFlows'
    && hasSameViewConfig(config, LEGACY_FIRST_FLOWS_DEFAULT);
}

export function getDashboardViewDefault(viewId) {
  const view = DASHBOARD_VIEWS[viewId] || DASHBOARD_VIEWS[DEFAULT_ACTIVE_VIEW_ID];
  return { layoutId: view.layoutId, panels: clonePanels(view.panels) };
}

function normalizeViewConfigs(
  viewConfigs,
  { dropCurrentDefaults = false, dropLegacyFirstFlows = false } = {},
) {
  if (!viewConfigs || typeof viewConfigs !== 'object' || Array.isArray(viewConfigs)) {
    return {};
  }

  return Object.entries(viewConfigs).reduce((configs, [viewId, config]) => {
    if (!isKnownViewId(viewId)) return configs;
    if (isValidViewConfig(config)) {
      const normalized = { layoutId: config.layoutId, panels: clonePanels(config.panels) };
      if (dropCurrentDefaults && isCurrentViewDefault(viewId, normalized)) return configs;
      if (dropLegacyFirstFlows && isLegacyFirstFlowsDefault(viewId, normalized)) {
        return configs;
      }
      configs[viewId] = normalized;
    }
    return configs;
  }, {});
}

function normalizeReplaySettings(config) {
  const replayTradingDate = /^\d{4}-\d{2}-\d{2}$/.test(config.replayTradingDate || '')
    ? config.replayTradingDate
    : null;
  const replayTradingDateManual = Boolean(replayTradingDate && config.replayTradingDateManual);
  return {
    replayTradingDate,
    replayTradingDateManual,
    replayTradingDateDiscovery: replayTradingDateManual
      && config.replayTradingDateDiscovery?.tradingDate === replayTradingDate
      ? config.replayTradingDateDiscovery
      : null,
    replayHistoryPreloadEnabled: config.replayHistoryPreloadEnabled === undefined
      ? true
      : Boolean(config.replayHistoryPreloadEnabled),
  };
}

function normalizeConfig(config, { dropLegacyFirstFlows = false } = {}) {
  const activeViewId = isKnownViewId(config.activeViewId)
    ? config.activeViewId
    : DEFAULT_ACTIVE_VIEW_ID;
  const viewConfigs = normalizeViewConfigs(config.viewConfigs, {
    dropCurrentDefaults: true,
    dropLegacyFirstFlows,
  });
  const activeViewConfig = viewConfigs[activeViewId] || getDashboardViewDefault(activeViewId);
  return {
    activeViewId,
    layoutId: activeViewConfig.layoutId,
    panels: activeViewConfig.panels,
    viewConfigs,
    ...normalizeReplaySettings(config),
  };
}

function migrateV5Config(config) {
  const activeViewId = isKnownViewId(config.activeViewId)
    ? config.activeViewId
    : DEFAULT_ACTIVE_VIEW_ID;
  const savedViewConfig = isValidViewConfig(config)
    ? { layoutId: config.layoutId, panels: clonePanels(config.panels) }
    : getDashboardViewDefault(activeViewId);
  return normalizeConfig({
    activeViewId,
    layoutId: savedViewConfig.layoutId,
    panels: savedViewConfig.panels,
    viewConfigs: { [activeViewId]: savedViewConfig },
    ...normalizeReplaySettings(config),
  }, { dropLegacyFirstFlows: true });
}

export const DEFAULT_CONFIG = normalizeConfig({
  activeViewId: DEFAULT_ACTIVE_VIEW_ID,
  viewConfigs: {},
  replayTradingDate: null,
  replayTradingDateManual: false,
  replayTradingDateDiscovery: null,
  replayHistoryPreloadEnabled: true,
});

function cloneConfig(config) {
  return {
    ...config,
    panels: clonePanels(config.panels),
    viewConfigs: Object.fromEntries(
      Object.entries(config.viewConfigs).map(([viewId, viewConfig]) => [
        viewId,
        { layoutId: viewConfig.layoutId, panels: clonePanels(viewConfig.panels) },
      ])
    ),
  };
}

function persistedConfig(config) {
  return {
    activeViewId: config.activeViewId,
    viewConfigs: config.viewConfigs,
    replayTradingDate: config.replayTradingDate,
    replayTradingDateManual: config.replayTradingDateManual,
    replayTradingDateDiscovery: config.replayTradingDateDiscovery,
    replayHistoryPreloadEnabled: config.replayHistoryPreloadEnabled,
  };
}

function writePersistedConfig(config) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedConfig(config)));
  } catch {
    // localStorage may be unavailable (private mode); fail silently.
  }
}

export function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return normalizeConfig(JSON.parse(raw));

    const v6Raw = localStorage.getItem(V6_STORAGE_KEY);
    if (v6Raw) {
      const migrated = normalizeConfig(JSON.parse(v6Raw), { dropLegacyFirstFlows: true });
      writePersistedConfig(migrated);
      return migrated;
    }

    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacyRaw) return cloneConfig(DEFAULT_CONFIG);

    const migrated = migrateV5Config(JSON.parse(legacyRaw));
    writePersistedConfig(migrated);
    return migrated;
  } catch {
    return cloneConfig(DEFAULT_CONFIG);
  }
}

function withActiveViewConfig(state, layoutId, panels) {
  const activeConfig = { layoutId, panels: clonePanels(panels) };
  return { ...state.viewConfigs, [state.activeViewId]: activeConfig };
}

let persistRevision = 0;

function persist(config) {
  persistRevision += 1;
  writePersistedConfig(config);
}

function persistSoon(config) {
  const revision = ++persistRevision;
  setTimeout(() => {
    if (revision !== persistRevision) return;
    writePersistedConfig(config);
  }, 0);
}

const initial = loadConfig();

const useDashboardConfig = create((set) => ({
  activeViewId: initial.activeViewId,
  layoutId: initial.layoutId,
  panels: initial.panels,
  viewConfigs: initial.viewConfigs,
  replayTradingDate: initial.replayTradingDate,
  replayTradingDateManual: initial.replayTradingDateManual,
  replayTradingDateDiscovery: initial.replayTradingDateDiscovery,
  replayHistoryPreloadEnabled: initial.replayHistoryPreloadEnabled,
  latestReplayTradingDate: null,

  // Atomic dashboard-level view switch. This prevents intermediate layouts or
  // panel assignments from being persisted while a preset is being applied.
  applyDashboardView: (view) =>
    set((state) => {
      const viewId = view?.id;
      if (!isKnownViewId(viewId)) return {};

      const savedViewConfig = state.viewConfigs[viewId];
      const viewConfig = savedViewConfig || getDashboardViewDefault(viewId);
      const layoutId = viewConfig.layoutId;
      const panels = clonePanels(viewConfig.panels);
      const viewConfigs = savedViewConfig
        ? { ...state.viewConfigs, [viewId]: { layoutId, panels } }
        : state.viewConfigs;
      const next = { ...state, activeViewId: viewId, layoutId, panels, viewConfigs };

      persist(next);

      return { activeViewId: viewId, layoutId, panels, viewConfigs };
    }),

  saveConfig: (config) =>
    set((state) => {
      if (
        !config?.viewConfigs
        || typeof config.viewConfigs !== 'object'
        || Array.isArray(config.viewConfigs)
        || !isKnownViewId(state.activeViewId)
      ) {
        return {};
      }

      const submittedViewConfigs = normalizeViewConfigs(config.viewConfigs);
      const viewConfigs = { ...state.viewConfigs, ...submittedViewConfigs };
      Object.entries(submittedViewConfigs).forEach(([viewId, viewConfig]) => {
        if (isCurrentViewDefault(viewId, viewConfig)) delete viewConfigs[viewId];
      });
      const activeViewConfig = viewConfigs[state.activeViewId]
        || getDashboardViewDefault(state.activeViewId);
      const layoutId = activeViewConfig.layoutId;
      const panels = clonePanels(activeViewConfig.panels);
      const replayTradingDate = /^\d{4}-\d{2}-\d{2}$/.test(config.replayTradingDate || '')
        ? config.replayTradingDate
        : state.replayTradingDate;
      const replayTradingDateManual = Boolean(
        replayTradingDate && config.replayTradingDateManual,
      );
      const replayTradingDateDiscovery = replayTradingDateManual
        && state.replayTradingDateDiscovery?.tradingDate === replayTradingDate
        ? state.replayTradingDateDiscovery
        : null;
      const replayHistoryPreloadEnabled = config.replayHistoryPreloadEnabled === undefined
        ? state.replayHistoryPreloadEnabled
        : Boolean(config.replayHistoryPreloadEnabled);
      const next = {
        ...state,
        layoutId,
        panels,
        viewConfigs,
        replayTradingDate,
        replayTradingDateManual,
        replayTradingDateDiscovery,
        replayHistoryPreloadEnabled,
      };

      persist(next);

      return {
        layoutId,
        panels,
        viewConfigs,
        replayTradingDate,
        replayTradingDateManual,
        replayTradingDateDiscovery,
        replayHistoryPreloadEnabled,
      };
    }),

  setActiveTab: (panelId, widgetId) =>
    set((state) => {
      const panels = state.panels.map((panel) =>
        panel.id === panelId
          ? { ...panel, activeTab: widgetId }
          : panel
      );
      const viewConfigs = withActiveViewConfig(state, state.layoutId, panels);
      const next = { ...state, panels, viewConfigs };

      persistSoon(next);

      return { panels, viewConfigs };
    }),

  setReplayTradingDate: (replayTradingDate, { manual = true, discovery = null } = {}) =>
    set((state) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(replayTradingDate || '')) return {};
      const replayTradingDateDiscovery = manual && discovery?.tradingDate === replayTradingDate
        ? discovery
        : null;
      const next = {
        ...state,
        replayTradingDate,
        replayTradingDateManual: manual,
        replayTradingDateDiscovery,
      };

      persistSoon(next);

      return { replayTradingDate, replayTradingDateManual: manual, replayTradingDateDiscovery };
    }),

  applyLatestReplayTradingDate: (replayTradingDate, { advanceDefault = true } = {}) =>
    set((state) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(replayTradingDate || '')) {
        return {};
      }
      if (
        !advanceDefault
        || (state.replayTradingDateManual && state.replayTradingDate !== replayTradingDate)
      ) {
        return { latestReplayTradingDate: replayTradingDate };
      }
      const next = {
        ...state,
        replayTradingDate,
        replayTradingDateManual: false,
        replayTradingDateDiscovery: null,
        latestReplayTradingDate: replayTradingDate,
      };

      persistSoon(next);

      return {
        replayTradingDate,
        replayTradingDateManual: false,
        replayTradingDateDiscovery: null,
        latestReplayTradingDate: replayTradingDate,
      };
    }),

  setReplayHistoryPreloadEnabled: (replayHistoryPreloadEnabled) =>
    set((state) => {
      const enabled = Boolean(replayHistoryPreloadEnabled);
      const next = { ...state, replayHistoryPreloadEnabled: enabled };

      persist(next);

      return { replayHistoryPreloadEnabled: enabled };
    }),
}));

export default useDashboardConfig;
